/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import SEOCopyBlock from '@/components/SEOCopyBlock';
import { buildSEOCopyBlock } from '@/features/common/utils/buildSEOCopyBlock';
import { useSeoMetadata } from '@/features/common/api/getSeoMetadata';
import { useLabels } from '@/hooks/useLabels';
import { useProductList } from '@/features/shelf/context/useProductList';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useContentStackPLPData } from '../../api/getContentStackProductListingPageData';
import { useProductShelfResults } from '@/features/category/api/getProductShelfResults';
import { useProductShelfACESResults } from '@/features/category/api/getProductShelfACESResults';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';

const labelMap = {
  about: 'label_generic_about',
};

export const CMSPLPSeoCopy = () => {
  const router = useLegacyRouter();
  const labels = useLabels(labelMap);
  const { acesEnabled } = useAcesFlags();

  const { data: shelfData } = useProductList();
  const makeOrModelName = shelfData?.makeorModelName;
  const shopByUrlMake = shelfData?.shopByUrlMake;
  const shopByUrlModel = shelfData?.shopByUrlModel;
  const shopByUrlYear = shelfData?.shopByUrlYear;

  const useShelfResultsFunction = acesEnabled ? useProductShelfACESResults : useProductShelfResults;
  const { data: productShelfResultsData, isFetching: productShelfResultsIsFetching } =
    useShelfResultsFunction();

  const selectedFacetValue =
    productShelfResultsData?.productShelfResults &&
    'selectedFacetValueDisplayName' in productShelfResultsData.productShelfResults
      ? productShelfResultsData?.productShelfResults?.selectedFacetValueDisplayName
      : undefined;

  const { data: seoMetadataInfo } = useSeoMetadata({
    enabled: !productShelfResultsIsFetching && makeOrModelName !== undefined,
    vehicleMake: shopByUrlMake ?? undefined,
    vehicleModel: shopByUrlModel ?? undefined,
    vehicleYear: shopByUrlYear ?? undefined,
    selectedFacetValue,
  });

  const isMerchShelfPage = router.route === '/deals/[cmsPath]/[[...slug]]';
  const { data: plpCMSData } = useContentStackPLPData({ enabled: true });
  const merchSeoCopyBlock = isMerchShelfPage
    ? plpCMSData?.shelfresultbottom?.find((block) => !!block.copy_block)
    : null;

  const shelfSeoCopyBlock = seoMetadataInfo?.topContentSeoActive
    ? seoMetadataInfo.copyBlockTwo
    : buildSEOCopyBlock(
        seoMetadataInfo?.copyBlockOne ?? null,
        seoMetadataInfo?.copyBlockTwo ?? null,
      );

  const seoCopyBlock = shelfSeoCopyBlock ?? merchSeoCopyBlock?.copy_block?.copy;

  const shelfHeading = seoMetadataInfo?.pageHeadingName ?? shelfData?.shelfHeading;
  const seoCopyHeading = shelfHeading ?? merchSeoCopyBlock?.copy_block?.heading;

  if (!seoCopyBlock) {
    return null;
  }

  const title = seoMetadataInfo?.pageHeadingNameH2Line1 ?? labels.about;
  const subtitle = seoMetadataInfo?.pageHeadingNameH2Line2 ?? seoCopyHeading;

  return <SEOCopyBlock title={title} boldTitle={subtitle} seoCopyBlock={seoCopyBlock} />;
};
